import { Component, OnInit, Input, inject } from '@angular/core';
import {
  AdminAccountUMClient, ProfileQueryFilter, TagQueryItem, UserTagsUMClient, RolesUMClient, IProfileResponse, ProfileResponse,
  UpsertUserTagRequest
} from 'src/app/api/api-user-management.service.generated';
import { Observable, BehaviorSubject, lastValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { switchMap, map } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AccountSearchDialogComponent } from './account-search-dialog/account-search.dialog';
import { LoadingAwareComponent } from 'src/app/common/utils/loading-aware.component';
import { Advertiser } from 'src/app/api/api-campaigns.service.generated';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { AsyncPipe } from '@angular/common';
import { ButtonComponent, ConfirmDialogModel } from "wecheer-common";
import { MatProgressBar } from "@angular/material/progress-bar";

interface IAdvertiserUser extends IProfileResponse {
  organizationId?: string;
  advertiserId?: string;
  isOwner: boolean;
  isAdvertiser: boolean;
}
@Component({
  selector: 'app-advertiser-users',
  templateUrl: './advertiser-users.component.html',
  styleUrls: ['./advertiser-users.component.scss'],
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatMenuModule,
    AsyncPipe,
    ButtonComponent,
    MatProgressBar
  ]
})
export class OrganizationUsersComponent extends LoadingAwareComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private accountClient = inject(AdminAccountUMClient);
  private tagsClient = inject(UserTagsUMClient);
  private rolesClient = inject(RolesUMClient);

  isShowLoading = false;
  _advertiser: Advertiser;
  get advertiser() { return this._advertiser; }
  @Input() set advertiser(value: Advertiser) {
    this._advertiser = value;
    this.refresh();
  }

  readonly Roles = {
    owner: 'OrganizationOwner',
    advertiser: 'Advertiser'
  };

  refreshSubject = new BehaviorSubject<any>(null);
  users$: Observable<IAdvertiserUser[]>;

  constructor() {
    super();

    const getAdvertiserUsers = (advId: string) =>
      this.accountClient.searchAccounts(new ProfileQueryFilter({
        tags: [new TagQueryItem({ tag: 'AdvertiserId', value: advId })],
        roles: [this.Roles.advertiser]
      }));
    this.users$ = this.route.paramMap.pipe(
      map(paramMap => paramMap.get('id')),
      switchMap(advId => this.refreshSubject.pipe(map(_ => advId))),
      switchMap(advId => this.withLoading(getAdvertiserUsers(advId))),
      map(resp => resp.profiles
        .map(profile => this.mapToOrgUser(profile))
        .sort((a, b) => this.getProfileOrder(a) - this.getProfileOrder(b))
      )
    );
    this.isShowLoading = this.isLoading;
  }

  refresh() {
    this.refreshSubject.next(new Date());
  }

  assertType(element: IAdvertiserUser): IAdvertiserUser {
    return element;
  }

  private mapToOrgUser(profile: ProfileResponse) {
    const ret: IAdvertiserUser = {
      ...profile,
      isOwner: profile.roles && profile.roles.includes(this.Roles.owner),
      isAdvertiser: profile.roles && profile.roles.includes(this.Roles.advertiser),
    };
    if (!profile.tags)
      return ret;

    const orgTag = profile.tags.find(t => t.tag === 'OrganizationId');
    ret.organizationId = orgTag && orgTag.value || undefined;

    const advTag = profile.tags.find(t => t.tag === 'AdvertiserId');
    ret.advertiserId = advTag && advTag.value || undefined;
    return ret;
  }
  private getProfileOrder(p: IAdvertiserUser) {
    if (!p.roles || p.roles.length === 0) return 3;
    if (p.roles.length > 1) return 2;
    return p.roles[0] === this.Roles.owner ? 0 : 1;
  }

  ngOnInit() {
  }

  async addAdvertiser() {
    const result = await AccountSearchDialogComponent.open(this.dialog, undefined);
    if (result) {
      this.isLoading = true;
      await lastValueFrom(this.tagsClient.upsertUserTag(new UpsertUserTagRequest({
        accountId: result.id,
        tag: 'AdvertiserId',
        value: this.advertiser.id
      })));
      await lastValueFrom(this.rolesClient.addUserRole(result.id, this.Roles.advertiser));
      this.refresh();
      this.isLoading = false;
    }
  }

  async onRemove(account: IAdvertiserUser, removeAdvertiser: boolean, _removeOwner: boolean) {
    const dialogModel = ConfirmDialogModel.danger(
      'This action cannot be undone. Are you absolutely sure?',
      'Delete the user?'
    );
    const confirmed = await dialogModel.show(this.dialog);
    if (!confirmed) return;

    this.isLoading = true;
    if (removeAdvertiser) {
      await lastValueFrom(this.rolesClient.removeUserRole(account.id, this.Roles.advertiser));

      const tagToRemove = account.tags.find(x => x.tag === 'AdvertiserId');
      if (tagToRemove !== undefined) {
        await lastValueFrom(this.tagsClient.removeUserTag(tagToRemove.id));
      }
    }

    this.refresh();
    this.isLoading = false;
  }
}
